import { Box, Heading, Text, Flex, useColorModeValue } from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import React from 'react';

import type { Transaction } from 'types/api/transaction';

import { route } from 'nextjs-routes';

import config from 'configs/app';
import { getDisplayTxNativeValueWei } from 'lib/explorer/displayOverrides';
import { useCoinPrice } from 'lib/contexts/CoinPriceContext';
import getValueWithUnit from 'lib/getValueWithUnit';
import getTxErrorText from 'lib/tx/getTxErrorText';
import useResolvedTxErrorText from 'lib/tx/useResolvedTxErrorText';
import { currencyUnits } from 'lib/units';
import AddressEntity from 'ui/shared/entities/address/AddressEntity';
import LinkInternal from 'ui/shared/links/LinkInternal';
import TxFee from 'ui/shared/tx/TxFee';

const TxAdditionalInfoContent = ({ tx }: { tx: Transaction }) => {
  const { dscPrice } = useCoinPrice();
  const effectiveExchangeRate = tx.exchange_rate || (dscPrice > 0 ? dscPrice : null);
  const errorText = useResolvedTxErrorText(tx) || getTxErrorText(tx);

  const sectionBorder = useColorModeValue('#e9ecef', 'gray.600');
  const labelColor = useColorModeValue('#212529', 'white');
  const valueColor = useColorModeValue('#495057', 'gray.300');
  const mutedColor = useColorModeValue('#6c757d', 'gray.400');
  const successColor = useColorModeValue('#28a745', 'green.400');
  const errorColor = useColorModeValue('#dc3545', 'red.400');
  const pendingBg = useColorModeValue('#ffc107', 'yellow.400');
  const pendingText = useColorModeValue('#856404', 'yellow.200');

  const sectionProps = {
    borderBottom: '1px solid',
    borderColor: sectionBorder,
    pb: 3,
    mb: 3,
  };

  const labelProps = {
    fontSize: '13px',
    fontWeight: 600,
    color: labelColor,
    mb: 1,
  };

  const valueProps = {
    fontSize: '13px',
    color: valueColor,
  };

  // Calculate confirmations (approximate)
  const confirmations = tx.confirmations || 0;

  // Transaction value in ETH
  const displayValueWei = getDisplayTxNativeValueWei(tx.hash, tx.value);
  const valueInEth = displayValueWei ? new BigNumber(displayValueWei).dividedBy(new BigNumber(10).pow(config.chain.currency.decimals)) : new BigNumber(0);

  return (
    <Box p={ 2 } minW="320px">
      <Heading as="h4" size="sm" mb={ 4 } color={ labelColor }>
        Additional Info
      </Heading>

      {/* Status */}
      <Box { ...sectionProps }>
        <Text { ...labelProps }>Status:</Text>
        <Flex alignItems="center" gap={ 2 }>
          { tx.status === 'ok' ? (
            <>
              <Box w="8px" h="8px" borderRadius="50%" bg={ successColor }/>
              <Text { ...valueProps } color={ successColor } fontWeight="500">
                Success
              </Text>
              { confirmations > 0 && (
                <Text fontSize="12px" color={ mutedColor }>
                  ({ confirmations } Block Confirmations)
                </Text>
              ) }
            </>
          ) : tx.status === 'error' ? (
            <>
              <Box w="8px" h="8px" borderRadius="50%" bg={ errorColor }/>
              <Text { ...valueProps } color={ errorColor } fontWeight="500">
                Failed
              </Text>
            </>
          ) : (
            <>
              <Box w="8px" h="8px" borderRadius="50%" bg={ pendingBg }/>
              <Text { ...valueProps } color={ pendingText } fontWeight="500">
                Pending
              </Text>
            </>
          ) }
        </Flex>
        { errorText && (
          <Text mt={ 2 } fontSize="12px" color={ errorColor } whiteSpace="normal">
            Reason: { errorText }
          </Text>
        ) }
      </Box>

      {/* Transaction Action */}
      { tx.to && (
        <Box { ...sectionProps }>
          <Text { ...labelProps }>Transaction Action:</Text>
          <Text { ...valueProps }>
            Transfer { valueInEth.toFormat(8) } { currencyUnits.ether }
            { effectiveExchangeRate && ` ($${valueInEth.multipliedBy(effectiveExchangeRate).toFormat(2)})` } to
          </Text>
          <AddressEntity
            address={ tx.to }
            fontSize="13px"
            truncation="constant"
            noIcon
          />
        </Box>
      ) }

      {/* Transaction Fee */}
      { !config.UI.views.tx.hiddenFields?.tx_fee && tx.fee?.value && (
        <Box { ...sectionProps }>
          <Text { ...labelProps }>Transaction Fee:</Text>
          <TxFee tx={ tx } withUsd accuracyUsd={ 2 } rowGap={ 0 }/>
        </Box>
      ) }

      {/* Gas Info */}
      { tx.gas_used !== null && (
        <Box { ...sectionProps }>
          <Text { ...labelProps }>Gas Info:</Text>
          <Text { ...valueProps }>
            { BigNumber(tx.gas_used).toFormat() } gas used from { BigNumber(tx.gas_limit).toFormat() } limit
          </Text>
          { tx.gas_price && (
            <Text fontSize="12px" color={ mutedColor }>
              @ { getValueWithUnit(tx.gas_price, 'ether').toFormat() } { currencyUnits.ether } ({ getValueWithUnit(tx.gas_price, 'gwei').toFormat() } Gwei)
            </Text>
          ) }
        </Box>
      ) }

      {/* Nonce */}
      <Box { ...sectionProps }>
        <Text { ...labelProps }>Nonce:</Text>
        <Text { ...valueProps }>
          { tx.nonce }
          { tx.position !== undefined && tx.position !== null && (
            <Text as="span" color={ mutedColor }> (in the position { tx.position })</Text>
          ) }
        </Text>
      </Box>

      {/* See more details link */}
      <LinkInternal
        href={ route({ pathname: '/tx/[hash]', query: { hash: tx.hash } }) }
        fontSize="13px"
        color="link_accent"
        display="flex"
        alignItems="center"
        gap="4px"
        _hover={{ textDecoration: 'underline' }}
      >
        See more details →
      </LinkInternal>
    </Box>
  );
};

export default React.memo(TxAdditionalInfoContent);
